import classNames from "classnames";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { TableUI } from "components/design/TableUI";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { DeleteIcon } from "components/icons/delete";
import { PlusIcon } from "components/icons/PlusIcon";
import dayjs from "dayjs";
import { useMemo, useRef, useState } from "react";
import { useFormikContext } from "formik";
import BankWarningCircle from "static/images/BankWarningCircle.svg";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { Info } from "components/icons/Info";
import { Button } from "components/DesignSystem/Button/Button";
import { Check } from "components/icons/Check";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { PriceInput } from "components/PriceInput/PriceInput";
import { JEItem, UnbalanceJE } from "store/apis/journalEntry";

const createEmptyEntry = (): JEItem => ({
  description: "",
  vendor: "",
  category: null,
  amount: 0,
});

type EntryTableMeta = {
  onDeleteEntry: (index: number) => void;
  getFieldName: (index: number) => string;
};

const createEntry = createColumnHelper<JEItem>();

const entryColumns = [
  createEntry.accessor("description", {
    size: 30,
    header: () => <HeadCellContent title="DESCRIPTION" required />,
    cell: (info) => {
      const { getFieldName } = info.table.options.meta as EntryTableMeta;
      const index = info.row.index;
      return (
        <TextInput
          name={`${getFieldName(index)}.description`}
          placeholder="Description"
        />
      );
    },
  }),
  createEntry.accessor("vendor", {
    size: 25,
    header: "VENDOR",
    cell: (info) => {
      const { getFieldName } = info.table.options.meta as EntryTableMeta;
      const index = info.row.index;
      return (
        <TextInput
          name={`${getFieldName(index)}.vendor`}
          placeholder="Vendor"
          hideError
        />
      );
    },
  }),
  createEntry.accessor("category", {
    size: 25,
    header: () => <HeadCellContent title="CATEGORY" required />,
    cell: (info) => {
      const { getFieldName } = info.table.options.meta as EntryTableMeta;
      const index = info.row.index;
      return (
        <CategorySelector
          name={`${getFieldName(index)}.category.uuid`}
          category={info.getValue()?.uuid ?? null}
          hideAction
          size="regular"
          required
        />
      );
    },
  }),
  createEntry.accessor("amount", {
    size: 15,
    header: () => <HeadCellContent title="AMOUNT" required align="right" />,
    cell: (info) => {
      const { getFieldName } = info.table.options.meta as EntryTableMeta;
      const index = info.row.index;
      return (
        <PriceInput
          name={`${getFieldName(index)}.amount`}
          rightAlign
          allowNegative
        />
      );
    },
  }),
  createEntry.display({
    id: "action",
    size: 5,
    header: "",
    cell: (info) => {
      const { onDeleteEntry } = info.table.options.meta as EntryTableMeta;
      const index = info.row.index;
      return (
        <Button
          customType="ghost_icon"
          type="button"
          onClick={() => onDeleteEntry(index)}
        >
          <DeleteIcon />
        </Button>
      );
    },
  }),
];

const UnbalancedAmount = ({ amount }: { amount: number }) => {
  const isZero = amount === 0;
  return (
    <div className="t-flex t-items-center t-gap-1">
      <div className="t-text-body-sm t-text-text-60">Unbalanced Amount:</div>
      <div
        className={classNames("t-text-body-sm", {
          "t-text-red-50": !isZero,
          "t-text-neutral-30": isZero,
        })}
      >
        <AmountSuperScript amount={amount} />
      </div>
    </div>
  );
};

const GroupAccordion = ({
  groupIndex,
  openItems,
}: {
  groupIndex: number;
  openItems: string[];
}) => {
  const { values, setFieldValue } = useFormikContext<{
    journalEntries: UnbalanceJE[];
  }>();
  const group = values.journalEntries[groupIndex];

  const groupRef = useRef(group);
  groupRef.current = group;

  const allEntries = useMemo(
    () => [...group.journal_items, ...(group.manual_entries || [])],
    [group.journal_items, group.manual_entries]
  );

  const groupBalance = parseFloat(
    allEntries.reduce((sum, e) => sum + (e.amount || 0), 0).toFixed(2)
  );
  const isBalanced = groupBalance === 0;
  const unbalancedAmount = Math.abs(groupBalance);
  const accordionKey = `${group.journal_no}`;
  const isOpen = openItems.includes(accordionKey);

  const stableMeta = useMemo(
    () => ({
      onDeleteEntry: (index: number) => {
        const g = groupRef.current;
        const unmatched = g.journal_items.length;
        if (index < unmatched) {
          const newEntries = [...g.journal_items];
          newEntries.splice(index, 1);
          setFieldValue(`journalEntries[${groupIndex}]`, {
            ...g,
            journal_items: newEntries,
          });
        } else {
          const manualIndex = index - unmatched;
          const newManual = [...(g.manual_entries || [])];
          newManual.splice(manualIndex, 1);
          setFieldValue(`journalEntries[${groupIndex}]`, {
            ...g,
            manual_entries: newManual,
          });
        }
      },
      getFieldName: (index: number) => {
        const unmatched = groupRef.current.journal_items.length;
        if (index < unmatched) {
          return `journalEntries[${groupIndex}].journal_items[${index}]`;
        }
        return `journalEntries[${groupIndex}].manual_entries[${
          index - unmatched
        }]`;
      },
    }),
    [groupIndex, setFieldValue]
  );

  const handleAddMore = () => {
    setFieldValue(`journalEntries[${groupIndex}]`, {
      ...group,
      manual_entries: [...(group.manual_entries || []), createEmptyEntry()],
    });
  };

  const defaultColumn = useMemo(() => ({ minSize: 1, maxSize: 100 }), []);

  const table = useReactTable({
    data: allEntries,
    columns: entryColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn,
    meta: stableMeta as EntryTableMeta,
  });

  return (
    <Accordion.Item value={accordionKey}>
      <Accordion.Trigger>
        <div className="t-flex t-justify-between t-w-full">
          <div className="t-flex t-items-center t-gap-2">
            {isBalanced ? (
              <Check className="t-size-5 t-text-dark_green" />
            ) : (
              <img
                src={BankWarningCircle}
                alt="BankWarningCircle"
                className="t-size-5"
              />
            )}
            <span className="t-text-subtext t-text-text-60">
              {dayjs(group.date).format("MMM DD, YYYY")}
            </span>
            <span className="t-text-neutral-30">•</span>
            <span className="t-text-subtext t-text-text-60">
              {group.journal_title || ""}
            </span>
            <span className="t-text-neutral-30">•</span>
            <span className="t-text-subtext t-text-text-60">
              {group.journal_no || ""}
            </span>
          </div>
          {unbalancedAmount !== 0 && (
            <UnbalancedAmount amount={unbalancedAmount} />
          )}
        </div>
      </Accordion.Trigger>
      <Accordion.Content forceMount className={isOpen ? "t-block" : "t-hidden"}>
        <div className="t-flex t-flex-col t-gap-3 t-pt-2">
          <TableUI table={table} />
          <span>
            <Button
              customType="ghost"
              size="small"
              type="button"
              onClick={handleAddMore}
              leftIcon={<PlusIcon size="12" />}
            >
              Add more
            </Button>
          </span>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};

export const ReviewStep = () => {
  const { values } = useFormikContext<{ journalEntries: UnbalanceJE[] }>();
  const [openItems, setOpenItems] = useState<string[]>([]);

  return (
    <div className="t-flex t-flex-col t-gap-5">
      <div className="t-flex t-flex-col t-gap-1">
        <div className="t-text-body-sm t-text-text-60">
          Please review the entries below and correct the missing debit or
          credit lines so that each journal is balanced
        </div>
        <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30">
          <Info />
          No entries have been added yet. All the entries will be posted only
          after balancing to zero
        </div>
      </div>

      <Accordion.Root
        type="multiple"
        value={openItems}
        onValueChange={setOpenItems}
      >
        <div className="t-flex t-flex-col t-gap-5">
          {values.journalEntries.map((journalEntry, i) => (
            <GroupAccordion
              key={`${journalEntry.date}-${journalEntry.journal_no}-${i}`}
              openItems={openItems}
              groupIndex={i}
            />
          ))}
        </div>
      </Accordion.Root>
    </div>
  );
};
